import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsBoost from "highcharts/modules/boost";
import highchartsMore from "highcharts/highcharts-more";
import { darkTheme, lightTheme } from "../../utils/Analytics/UseAnalytics";
import "./DigitalSignageViewContainer.scss";
import moment from "moment";
import { Loader } from "@scuf/common";
import {
  formatCurrency,
  getMaxConvertedValueFromArray,
} from "@EMaaS/EMaaS.consup.react.widget.energymanagement";

highchartsMore(Highcharts);
HighchartsBoost(Highcharts);
interface IDigitalSignageAreaGraphProps {
  theme: boolean; // Optional theme prop
  tab: number; // Tab name for the chart
  data: IPerformanceData;
  unit: string; // Unit of measurement for the data
  type: string; // Type of data (e.g., electricity, gas, etc.)
}
export interface IEnergyConsumptionData {
  startDate: string; // ISO date string
  value: number | null; // Actual consumption value
}
export interface IPerformanceData {
  actualPerformance: IEnergyConsumptionData[];
  baselinePerformance: IEnergyConsumptionData[];
}
interface IResizeOfGraph {
  lineWidthBaseLineResize: number; // Line width for the baseline
  lineHeightActualResize: number; // Line height for the actual series
  widthActualResize: number; // Width for the actual series
  radiusActualResize: number; // Radius for the markers in the actual series
  lineWidthActualResize: number; // Line width for the markers in the actual series
  offsetYActualResize: number; // Vertical offset for the shadow in the actual series
  opacityActualResize: number; // Opacity for the shadow in the actual series
  markerLineWidthResize: number; // Line width for the markers in the actual series
  highChartMarginTop: number; // Margin top for the chart
  orientation: string; // Orientation of the screen (landscape or portrait)
  chartMarginRight?: number; // Optional margin right for the chart
  chartMarginRightLandscape?: number; // Optional margin right for the chart in landscape mode
  xAxisLabelGap?: number; // Optional gap for the x-axis labels
  highChartContainerWidth?: string; // Optional height for the chart container
}
const DigitalSignageAreaGraph = (props: IDigitalSignageAreaGraphProps) => {
  const { theme, tab, data } = props; // Destructure the props
  const [getGraphData, setGraphData] = useState<{
    xAxisCategories: string[];
    actualSeriesData: number[];
    baselineSeriesData: number[];
    dateBaselineRange: string;
    dateActualRange: string;
  }>({
    xAxisCategories: [],
    actualSeriesData: [],
    baselineSeriesData: [],
    dateBaselineRange: "",
    dateActualRange: "",
  }); // State to hold the data
  const [resizeOfGraph, setResizeOfGraph] = useState<IResizeOfGraph>({
    lineWidthBaseLineResize: 3,
    lineHeightActualResize: 12,
    widthActualResize: 12,
    markerLineWidthResize: 5,
    radiusActualResize: 8,
    lineWidthActualResize: 12,
    offsetYActualResize: 5,
    opacityActualResize: 0.3,
    highChartMarginTop: 15,
    orientation: "landscape", // Default orientation
    chartMarginRight: 0, // Default margin right
    chartMarginRightLandscape: 0, // Default margin right for landscape mode
    xAxisLabelGap: undefined, // Default x-axis label gap
    highChartContainerWidth: "100%", // Default height for the chart container
  }); // State to track if the graph is resized
  const graphDataFormation = (data: IPerformanceData) => {
    // Process the data to extract xAxis categories and series data
    const processedActualData =
      data.actualPerformance.length > 0
        ? data.actualPerformance.map((item) => {
            const date = moment(item.startDate); // Parse the ISO date string
            const month = date.format("MMM"); // Get the month abbreviation (e.g., "Jan")
            const year = date.format("YY"); // Get the last two digits of the year (e.g., "24")
            const yearYYYY = moment(item.startDate).year();
            return {
              month: month === "Jan" ? `${month} ${year}` : month, // Append year only for January
              actual: item.value ?? 0, // Use nullish coalescing operator to handle undefined values
              fullYear: yearYYYY, // Add the full year for sorting
            };
          })
        : []; // Initialize processedData as an empty array if no data is available

    const processedBaselineData =
      data.baselinePerformance.length > 0
        ? data.baselinePerformance.map((item) => {
            const date = moment(item.startDate); // Parse the ISO date string
            const month = date.format("MMM"); // Get the month abbreviation (e.g., "Jan")
            const year = date.format("YY"); // Get the last two digits of the year (e.g., "24")
            const yearYYYY = moment(item.startDate).year();
            return {
              month: month === "Jan" ? `${month} ${year}` : month, // Append year only for January
              baseline:item.value ?? 0, // Use nullish coalescing operator to handle undefined values
              fullYear: yearYYYY, // Add the full year for sorting
            };
          })
        : []; // Initialize processedData as an empty array if no data is available

    // Generate the date range (e.g., "Mar 23 - Feb 24")
    const startActualDate = data.actualPerformance[0]?.startDate ? moment(data.actualPerformance[0]?.startDate) : '--';
    const endActualDate = data.actualPerformance.length > 0 && data.actualPerformance[data.actualPerformance.length - 1]?.startDate
      ? moment(data.actualPerformance[data.actualPerformance.length - 1]?.startDate)
      : '--';

    const dateActualRange =
      startActualDate !== '--' && endActualDate !== '--'
        ? `${(startActualDate as moment.Moment).format("MMM YY")} - ${(endActualDate as moment.Moment).format("MMM YY")}`
        : '-- - --'; // Format the date range for actual performance
    // Generate the date range (e.g., "Mar 23 - Feb 24")
    const startBaselineDate = data.baselinePerformance[0]?.startDate ? moment(data.baselinePerformance[0]?.startDate) : '--';
    const endBaselineDate = moment(
      data.baselinePerformance[data.baselinePerformance.length - 1]?.startDate
    );
    const dateBaselineRange =
      startBaselineDate !== '--'
        ? `${(startBaselineDate as moment.Moment).format("MMM YY")} - ${endBaselineDate.format("MMM YY")}`
        : '-- - --';

    const xAxisCategories =
      processedActualData.length > 0
        ? processedActualData?.map((item) => item.month)
        : processedBaselineData?.map((item) => item.month); // Extract months for xAxis
    const actualSeriesData =
      processedActualData.length > 0
        ? processedActualData?.map((item) => item.actual)
        : []; // Extract actualConsumption for ACTUAL series
    const baselineSeriesData =
      processedBaselineData.length > 0
        ? processedBaselineData?.map((item) => item.baseline)
        : []; // Extract baselineConsumption for BASELINE series
    return {
      xAxisCategories,
      actualSeriesData,
      baselineSeriesData,
      dateActualRange,
      dateBaselineRange,
    };
  };

  useEffect(() => {
    if (
      data.actualPerformance.length === 0 &&
      data.baselinePerformance.length === 0
    ) {
      setGraphData({
        xAxisCategories: [],
        actualSeriesData: [],
        baselineSeriesData: [],
        dateBaselineRange: "",
        dateActualRange: "",
      });
    } else {
      // Fetch data when the component mounts or when the data prop changes
      let graphData = graphDataFormation(data);
      setGraphData(graphData); // Set the processed data to state
    }
  }, [data]); // Dependency array to trigger useEffect when data changes

  const getDynamicGradientStops = (tab: number) => {
    let firstGradient,
      secondGradient,
      legendActualColor,
      baseLineColor,
      actualIcon,
      baseLineIcon,
      actualGraStartColor,
      actualGraEndColor;
    switch (tab) {
      case 0: // Electricity Tab
        firstGradient = [
          [0, "rgba(255, 165, 247, 0.2)"], // Start color
          [1, "rgba(255, 165, 247, 0.05)"], // End color
        ];
        secondGradient = [
          [0, "rgba(255, 165, 247, 0.05)"], // Start color
          [1, theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"], // End color
        ];
        legendActualColor = theme ? "#FFA5F7" : "#BE2BBB";
        baseLineColor = theme ? "#FFA5F7" : "#BE2BBB";
        actualIcon = "legendElecActualBox";
        baseLineIcon = theme
          ? "legendElecBaselineBox legendElecBaselineDarkBox"
          : "legendElecBaselineBox legendElecBaselineLightBox";
        actualGraStartColor = "#8B6EDD";
        actualGraEndColor = theme ? "#D021C0" : "#BE2BBB";
        return {
          firstGradient,
          secondGradient,
          legendActualColor,
          baseLineColor,
          actualIcon,
          baseLineIcon,
          actualGraStartColor,
          actualGraEndColor,
        };
      case 1: // Gas Tab
        firstGradient = [
          [0, "rgba(255, 209, 134, 0.2)"], // Start color
          [1, "rgba(255, 209, 134, 0.05)"], // End color
        ];
        secondGradient = [
          [0, "rgba(255, 209, 134, 0.05)"], // Start color
          [1, theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"], // End color
        ];
        legendActualColor = theme ? "#FFD186" : "#C37900";
        baseLineColor = theme ? "#FFD186" : "#C37900";
        actualIcon = "legendGasActualBox";
        baseLineIcon = theme
          ? "legendGasBaselineBox legendGasBaselineDarkBox"
          : "legendGasBaselineBox legendGasBaselineLightBox";
        actualGraStartColor = "#FFD186";
        actualGraEndColor = "#C37900";
        return {
          firstGradient,
          secondGradient,
          legendActualColor,
          baseLineColor,
          actualIcon,
          baseLineIcon,
          actualGraStartColor,
          actualGraEndColor,
        };
      case 2: // Water Tab
        firstGradient = [
          [0, "rgba(51, 214, 241, 0.2)"], // Start color
          [1, "rgba(51, 214, 241, 0.05)"], // End color
        ];
        secondGradient = [
          [0, "rgba(51, 214, 241, 0.05)"], // Start color
          [1, theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"], // End color
        ];
        legendActualColor = theme ? "#33D6F1" : "#087789";
        baseLineColor = theme ? "#33D6F1" : "#087789";
        actualIcon = "legendWaterActualBox";
        baseLineIcon = theme
          ? "legendWaterBaselineBox legendWaterBaselineDarkBox"
          : "legendWaterBaselineBox legendWaterBaselineLightBox";
        actualGraStartColor = "#33D6F1";
        actualGraEndColor = "#087789";
        return {
          firstGradient,
          secondGradient,
          legendActualColor,
          baseLineColor,
          actualIcon,
          baseLineIcon,
          actualGraStartColor,
          actualGraEndColor,
        };
      case 3: // Carbon Tab
        firstGradient = [
          [0, "rgba(29, 206, 155, 0.2)"], // Start color
          [1, "rgba(29, 206, 155, 0.05)"], // End color
        ];
        secondGradient = [
          [0, "rgba(29, 206, 155, 0.05)"], // Start color
          [1, theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"], // End color
        ];
        legendActualColor = theme ? "#1DCE9B" : "#138967";
        baseLineColor = theme ? "#1DCE9B" : "#138967";
        actualIcon = "legendCarbonActualBox";
        baseLineIcon = theme
          ? "legendCarbonBaselineBox legendCarbonBaselineDarkBox"
          : "legendCarbonBaselineBox legendCarbonBaselineLightBox";
        actualGraStartColor = "#1DCE9B";
        actualGraEndColor = "#138967";
        return {
          firstGradient,
          secondGradient,
          legendActualColor,
          baseLineColor,
          actualIcon,
          baseLineIcon,
          actualGraStartColor,
          actualGraEndColor,
        };
      case 4: // Savings Tab
        firstGradient = [
          [0, theme ? "rgba(218, 197, 197, 0.2)" : "rgba(203, 124, 120, 0.2)"], // Start color
          [
            1,
            theme ? "rgba(218, 197, 197, 0.05)" : "rgba(203, 124, 120, 0.05)",
          ], // End color
        ];
        secondGradient = [
          [
            0,
            theme ? "rgba(218, 197, 197, 0.05)" : "rgba(203, 124, 120, 0.05)",
          ], // Start color
          [1, theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"], // End color
        ];
        legendActualColor = theme ? "#CB7C78" : "#501413";
        baseLineColor = theme ? "#CB7C78" : "#501413";
        actualIcon = "legendSavingsActualBox";
        baseLineIcon = theme
          ? "legendSavingsBaselineBox legendSavingsBaselineDarkBox"
          : "legendSavingsBaselineBox legendSavingsBaselineLightBox";
        actualGraStartColor = theme ? "#DAC5C5" : "#CB7C78";
        actualGraEndColor = theme ? "#CB7C78" : "#501413";
        return {
          firstGradient,
          secondGradient,
          legendActualColor,
          baseLineColor,
          actualIcon,
          baseLineIcon,
          actualGraStartColor,
          actualGraEndColor,
        };
      default: // Default Electricity Tab
        firstGradient = [
          [0, "rgba(255, 165, 247, 0.2)"], // Start color
          [1, "rgba(255, 165, 247, 0.05)"], // End color
        ];
        secondGradient = [
          [0, "rgba(255, 165, 247, 0.05)"], // Start color
          [1, theme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"], // End color
        ];
        legendActualColor = theme ? "#FFA5F7" : "#BE2BBB";
        baseLineColor = theme ? "#FFA5F7" : "#BE2BBB";
        actualIcon = "legendElecActualBox";
        baseLineIcon = theme
          ? "legendElecBaselineBox legendElecBaselineDarkBox"
          : "legendElecBaselineBox legendElecBaselineLightBox";
        actualGraStartColor = "#8B6EDD";
        actualGraEndColor = theme ? "#D021C0" : "#BE2BBB";
        return {
          firstGradient,
          secondGradient,
          legendActualColor,
          baseLineColor,
          actualIcon,
          baseLineIcon,
          actualGraStartColor,
          actualGraEndColor,
        };
    }
  };
  // Get the dynamic gradient stops based on the current tab
  const dynamicGradientStops = getDynamicGradientStops(tab);
  // Define the gradient stops
  const gradientStops = [
    [0, dynamicGradientStops?.actualGraStartColor], // Start color of the gradient
    [1, dynamicGradientStops?.actualGraEndColor], // End color of the gradient
  ];
  // Function to calculate the color for a specific index
  const getGradientColorByIndex = (index: number, total: number) => {
    const ratio = index / (total - 1); // Normalize the index between 0 and 1
    const startColor = gradientStops[0][1];
    const endColor = gradientStops[1][1];

    // Interpolate between the start and end colors
    const interpolateColor = (start: string, end: string, factor: number) => {
      const startRGB = parseInt(start.slice(1), 16);
      const endRGB = parseInt(end.slice(1), 16);

      const r = Math.round(
        ((endRGB >> 16) - (startRGB >> 16)) * factor + (startRGB >> 16)
      );
      const g = Math.round(
        (((endRGB >> 8) & 0xff) - ((startRGB >> 8) & 0xff)) * factor +
          ((startRGB >> 8) & 0xff)
      );
      const b = Math.round(
        ((endRGB & 0xff) - (startRGB & 0xff)) * factor + (startRGB & 0xff)
      );

      return `#${((1 << 24) + (r << 16) + (g << 8) + b)
        .toString(16)
        .slice(1)
        .toUpperCase()}`;
    };

    return interpolateColor(String(startColor), String(endColor), ratio);
  };

  // Define the data dynamically with marker.lineColor based on the index
  const actualData = getGraphData?.actualSeriesData.map(
    (value, index, array) => ({
      y: value,
      marker: {
        lineColor: getGradientColorByIndex(index, array.length), // Dynamically calculate the color based on index
      },
    })
  );
  const [chartHeight, setChartHeight] = useState<number>(
    window.innerHeight * 0.5
  ); // Set initial height to 50% of screen height
  useEffect(() => {
    const handleResize = () => {
      setChartHeight(window.innerHeight * 0.5); // Adjust height dynamically on window resize
    };

    window.addEventListener("resize", handleResize); // Add event listener for window resize
    return () => {
      window.removeEventListener("resize", handleResize); // Cleanup event listener on unmount
    };
  }, []);

  // Adjust line width based on screen resolution
  useEffect(() => {
    const updateLineWidth = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      // console.log("Screen Width:", screenWidth, screenHeight); // Log the screen width for debugging
      // Safely get the orientation or calculate it as a fallback
      const orientation =
        window.screen.orientation?.type ||
        (screenWidth > screenHeight ? "landscape-primary" : "portrait-primary");
        
      if (screenWidth < 721) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 94,
          highChartContainerWidth: orientation.startsWith("portrait")? "44.9rem" : "100%",
        });
      }else if (screenWidth < 820) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 98,
          highChartContainerWidth: orientation.startsWith("portrait")? "48.3rem" : "100%",
        })
      } else if (screenWidth < 899) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 128,
          highChartContainerWidth: orientation.startsWith("portrait")? "54rem" : "100%",
        });
      }else if (screenWidth < 901) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 30,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 128,
          highChartContainerWidth: orientation.startsWith("portrait")? "57rem" : "100%",
        });
      } else if (screenWidth < 964) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 30,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 135,
          highChartContainerWidth: orientation.startsWith("portrait")? "60.4rem" : "100%",
        });
      } else if (screenWidth < 1024) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 40,
          highChartContainerWidth: "1360px",
        });
      }else if (screenWidth < 1082) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: orientation.startsWith("portrait") ? 25: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight:103,
          chartMarginRightLandscape: 50,
          highChartContainerWidth: orientation.startsWith("portrait") ? '65rem' : "1255px",
        });
      } else if (screenWidth < 1201) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 120,
          chartMarginRightLandscape: 50,
          highChartContainerWidth: orientation.startsWith("portrait") ? '74.6rem' : "1255px",
        });
      }else if (screenWidth < 1366) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 8,
          lineWidthActualResize: 12,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 130,
          chartMarginRightLandscape: 50,
          highChartContainerWidth: orientation.startsWith("portrait") ? '84rem' : "1255px",
        });
      } else if (screenWidth < 1423) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 9,
          lineWidthActualResize: 14,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 40,
          chartMarginRightLandscape: 58,
          highChartContainerWidth: "1400px",
        });
      }else if (screenWidth < 1537) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 9,
          lineWidthActualResize: 14,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 136,
          chartMarginRightLandscape: 100,
          highChartContainerWidth: orientation.startsWith("portrait")? "89.5rem" : "1534px",
        });
      } else if (screenWidth < 1601) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 3,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 9,
          lineWidthActualResize: 14,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 136,
          chartMarginRightLandscape: 90,
          highChartContainerWidth: orientation.startsWith("portrait")? "89.5rem" : "1598px",
        });
      } else if (screenWidth < 1800) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 4,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 11,
          lineWidthActualResize: 17,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 5,
          highChartMarginTop: 15,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 160,
          chartMarginRightLandscape: 106,
          highChartContainerWidth: orientation.startsWith("portrait")? "100.5rem" : "1704px",
        });
      } else if (screenWidth < 2001) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 4,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 12,
          lineWidthActualResize: 20,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 7,
          highChartMarginTop: 18,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 180,
          chartMarginRightLandscape: 116,
          xAxisLabelGap: 38,
          highChartContainerWidth:orientation.startsWith("portrait")? "100.5rem" : "1915px",
        });
      }else if (screenWidth < 2050) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 4,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 11,
          lineWidthActualResize: 16,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 20,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 190,
          chartMarginRightLandscape: 140,
          xAxisLabelGap: 38,
          highChartContainerWidth:orientation.startsWith("portrait")? "134.5rem" : "128rem",
        });
      }  else if (screenWidth < 2264) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 4,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 11,
          lineWidthActualResize: 16,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 20,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 190,
          chartMarginRightLandscape: 120,
          xAxisLabelGap: 38,
          highChartContainerWidth:orientation.startsWith("portrait")? "134.5rem" : "133rem",
        });
      } else if (screenWidth < 2305) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 5,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 22,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 24,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 135,
          xAxisLabelGap: 44,
          highChartContainerWidth:orientation.startsWith("portrait")? "100%" :  "143.8rem",
        });
      } else if (screenWidth < 2401) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 5,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 22,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 24,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 135,
          xAxisLabelGap: 44,
          highChartContainerWidth: "149.8rem",
        });
      } else if (screenWidth < 2561) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 5,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 22,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 25,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 150,
          xAxisLabelGap: 47,
          highChartContainerWidth: "159.5rem",
        });
      } else if (screenWidth < 2881) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 5,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 22,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 26,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 200,
          chartMarginRightLandscape: 180,
          xAxisLabelGap: 50,
          highChartContainerWidth: "179.7rem",
        });
      } else if (screenWidth < 3001) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 5,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 22,
          offsetYActualResize: 5,
          opacityActualResize: 0.3,
          markerLineWidthResize: 8,
          highChartMarginTop: 34,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 48,
          xAxisLabelGap: 70,
        });
      }else if (screenWidth < 3073) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 6,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 20,
          offsetYActualResize: 10,
          opacityActualResize: 0.6,
          markerLineWidthResize: 10,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 150,
          xAxisLabelGap: 72,
          highChartContainerWidth: "191.7rem",
        });
      } else if (screenWidth < 3201) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 6,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 20,
          offsetYActualResize: 10,
          opacityActualResize: 0.6,
          markerLineWidthResize: 10,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 140,
          xAxisLabelGap: 72,
          highChartContainerWidth: "199.7rem",
        });
      } else if (screenWidth < 3414) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 6,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 15,
          lineWidthActualResize: 20,
          offsetYActualResize: 10,
          opacityActualResize: 0.6,
          markerLineWidthResize: 10,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 300,
          chartMarginRightLandscape: 150,
          xAxisLabelGap: 72,

          highChartContainerWidth:orientation.startsWith("portrait")? "201.5rem" :  "213rem",
        });
      } else if (screenWidth < 3841) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 7,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 18,
          lineWidthActualResize: 25,
          offsetYActualResize: 10,
          opacityActualResize: 0.6,
          markerLineWidthResize: 13,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 230,
          xAxisLabelGap: 72,
          highChartContainerWidth: "239.5rem",
        });
      } else if (screenWidth < 4001) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 8,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 25,
          lineWidthActualResize: 40,
          offsetYActualResize: 10,
          opacityActualResize: 0.6,
          markerLineWidthResize: 15,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 80,
          xAxisLabelGap: 72,
        });
      }else if (screenWidth < 4610) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 8,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 23,
          lineWidthActualResize: 40,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 18,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 350,
          chartMarginRightLandscape: 270,
          xAxisLabelGap: 94,
          highChartContainerWidth:orientation.startsWith("portrait")? "269.5rem" :  "287.4rem",
        });
      } else if (screenWidth < 5200) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 8,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 23,
          lineWidthActualResize: 40,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 18,
          highChartMarginTop: orientation.startsWith("portrait") ? 40 : 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 350,
          chartMarginRightLandscape: 270,
          xAxisLabelGap: 94,
          highChartContainerWidth:orientation.startsWith("portrait")? "269.5rem" :  "319.4rem",
        });
      }else if (screenWidth < 6145) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 8,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 31,
          lineWidthActualResize: 52,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 20,
          highChartMarginTop: 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 320,
          xAxisLabelGap: 110,
          highChartContainerWidth: orientation.startsWith("portrait")? "100%" :"383.3rem",
        });
      }  else if (screenWidth < 6200) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 8,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 31,
          lineWidthActualResize: 52,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 20,
          highChartMarginTop: 40,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 320,
          xAxisLabelGap: 110,
          highChartContainerWidth: "359.3rem",
        });
      } else if (screenWidth < 7700) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 9,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 41,
          lineWidthActualResize: 62,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 30,
          highChartMarginTop: 70,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 360,
          xAxisLabelGap: 130,
        });
      } else if (screenWidth < 9700) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 9,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 41,
          lineWidthActualResize: 62,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 30,
          highChartMarginTop: 30,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 170,
          xAxisLabelGap: 150,
        });
      } else if (screenWidth < 15000) {
        setResizeOfGraph({
          lineWidthBaseLineResize: 16,
          lineHeightActualResize: 12,
          widthActualResize: 12,
          radiusActualResize: 51,
          lineWidthActualResize: 72,
          offsetYActualResize: 5,
          opacityActualResize: 1,
          markerLineWidthResize: 30,
          highChartMarginTop: 85,
          orientation: orientation.startsWith("portrait")
            ? "portrait"
            : "landscape",
          chartMarginRight: 100,
          chartMarginRightLandscape: 500,
          xAxisLabelGap: 150,
          highChartContainerWidth: "639.4rem",
        });
      }
    };

    updateLineWidth(); // Set initial line width
    window.addEventListener("resize", updateLineWidth); // Update on window resize

    return () => {
      window.removeEventListener("resize", updateLineWidth); // Cleanup event listener
    };
  }, []);

  const calculateYAxisRange = (
    actualData: number[],
    baselineData: number[]
  ) => {
    if (actualData.length === 0 && baselineData.length === 0) {
      return { min: 0, max: 100, tickInterval: 20 }; // Default range
    }

    const minActual = actualData.length > 0 ? Math.min(...actualData, 0) : 0; // Minimum value in ACTUAL series
    const minBaseline = baselineData.length > 0 ? Math.min(...baselineData) : 0; // Minimum value in BASELINE series
    const maxActual = actualData.length > 0 ? Math.max(...actualData, 0) : 0; // Maximum value in ACTUAL series
    const maxBaseline = baselineData.length > 0 ? Math.max(...baselineData) : 0; // Maximum value in BASELINE series

    const min = Math.min(minActual, minBaseline); // Take the smallest minimum
    const max = Math.max(maxActual, maxBaseline); // Take the largest maximum
    const range = max - min; // Calculate the range
    //const tickInterval = range / 4; // Divide range into 4 intervals for 5 slots
    // Round min down to the nearest multiple of 10
    const roundedMin = Math.floor(min / 10) * 10;

    // Round max up to the nearest multiple of 10
    const roundedMax = Math.ceil(max / 10) * 10 + Math.ceil(0.05 * Math.abs(max));;

    // Calculate tick interval and round it to the nearest multiple of 10
    const tickInterval = Math.ceil((roundedMax - roundedMin) / 4 / 10) * 10;

    return {
      min: roundedMin, // Round down the minimum value
      max: roundedMax, // Round up the maximum value . +1 to ensure the last tick is fully visible
      tickInterval: Math.ceil(tickInterval), // Round up the interval
    };
  };

  // Calculate Y-axis range dynamically based on both series
  const yAxisRange = calculateYAxisRange(
    getGraphData?.actualSeriesData,
    getGraphData?.baselineSeriesData
  );

  const chartOptions = {
    chart: {
      type: "areaspline",
      height: chartHeight, // Dynamically set the chart height
      marginTop: resizeOfGraph.highChartMarginTop, // Add extra space at the top to prevent cropping
      // marginLeft: 100, // Adjust this value to create space for Y-axis labels
      marginRight:
        resizeOfGraph.orientation === "portrait"
          ? resizeOfGraph.chartMarginRight
          : resizeOfGraph.chartMarginRightLandscape
          ? resizeOfGraph.chartMarginRightLandscape
          : 0, // Adjust this if the Y-axis is on the right
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false, // Disables the Highcharts watermark
    },
    xAxis: {
      categories: getGraphData?.xAxisCategories,
      tickmarkPlacement: "on",
      lineWidth: 0, // Removes the x-axis line
      labels: {
        y: resizeOfGraph.xAxisLabelGap
          ? resizeOfGraph.xAxisLabelGap
          : undefined, // Increase this value to add a gap between the X-axis labels and the graph
        style: {
          color: theme ? "#D0D0D0" : "#606060", // Set the color for x-axis values
        },
      },
      startOnTick: true, // Ensure the first tick is fully visible
      endOnTick: true, // Ensure the last tick is fully visible
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        x: 10, // Move the labels further to the right
        //y: 15, // Adjust vertical position to prevent cropping
        style: {
          color: theme ? "#D0D0D0" : "#606060", // Set the color for x-axis values
        },
        formatter: function (
          this: Highcharts.AxisLabelsFormatterContextObject
        ) {
          let maxValueUnitConversion, costValueUnitConversion;
          if (props.type === "cost") {
            costValueUnitConversion =
              this.value !== "undefined"
                ? formatCurrency(Number(this.value), props.unit)
                : "--";
          } else {
            maxValueUnitConversion = getMaxConvertedValueFromArray(
              props.type,
              props.unit,
              [Number(this.value)]
            );
          }
          return props.type === "cost"
            ? costValueUnitConversion
            : `${maxValueUnitConversion?.Value ?? "--"} ${
                maxValueUnitConversion?.Unit ?? ""
              }`; // Append the unit to the Y-axis labels
        },
      },

     // min: yAxisRange.min, // Dynamically set the minimum value
     // max: yAxisRange.max, // Dynamically set the maximum value
      tickAmount:5,
     // tickInterval: 4, // Dynamically set the tick interval
      opposite: true, // Moves the Y-axis to the right-hand side
      gridLineWidth: 0, // Removes the Y-axis grid lines
      softThreshold: false, // Ensure the axis includes all data points
    },
    legend: {
      useHTML: true, // Enables HTML rendering for the legend
      symbolWidth: 0, // Removes the default color box or icon

      labelFormatter: function (this: Highcharts.Point): string {
        // Assign different colors based on the series name
        const legendColor =
          this.name === "ACTUAL"
            ? dynamicGradientStops?.legendActualColor // Color for ACTUAL
            : theme
            ? "#D0D0D0"
            : "#4A4A4A"; // Color for BASELINE
        const iconClass =
          this.name === "ACTUAL"
            ? dynamicGradientStops?.actualIcon // Icon for ACTUAL
            : dynamicGradientStops?.baseLineIcon; // Icon for BASELINE
        // Customize the legend item with an image
        return `<div><div class="legend-container-ds"><div class="${iconClass}"></div><div class="legend-container-ds-basic-text"> <div class="legend-container-ds-text-Base">${
          this.name === "ACTUAL" ? "CURRENT" : "COMPARISON"
        }</div><div class="legend-container-ds-text-Base">(${
          this.name === "ACTUAL"
            ? getGraphData.dateActualRange
            : getGraphData.dateBaselineRange
        })</div> </div></div></div>`;
      },
    },
    tooltip: {
      enabled: false, // Disables the tooltip
    },
    series: [
      {
        name: "BASELINE",
        data: getGraphData?.baselineSeriesData,
        dashStyle: "Dash",
        color: "rgba(255, 255, 255, 0)",
        lineWidth: resizeOfGraph.lineWidthBaseLineResize,
        lineColor: dynamicGradientStops?.baseLineColor,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Vertical gradient for the area
          stops: dynamicGradientStops?.firstGradient,
        },
      },
      {
        name: "ACTUAL",
        //data: [83, 78, 98, 93, 106, 84, 105, 104, 91, 83, 64, 76],
        cropThreshold: 60000, // Prevent markers from being cropped
        data: actualData,
        color: theme ? "#404040" : "#ffffff",
        lineWidth: resizeOfGraph.lineWidthActualResize,
        lineColor: {
          linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
          stops: gradientStops,
        },
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, // Vertical gradient for the area
          stops: dynamicGradientStops?.secondGradient,
        },
        shadow: {
          color: "rgba(0, 0, 0, 0.5)", // Shadow color
          offsetX: 0, // Horizontal offset
          offsetY: resizeOfGraph.offsetYActualResize, // Vertical offset
          opacity: resizeOfGraph.opacityActualResize, // Shadow opacity
          width: resizeOfGraph.widthActualResize, // Shadow blur width
        },
        marker: {
          enabled: true,
          radius: resizeOfGraph.radiusActualResize,
          symbol: "circle",
          lineWidth: resizeOfGraph.markerLineWidthResize,
          clip: false, // Prevent markers from being clipped
        },
      },
    ],
  };
  // Apply the selected theme
  const setTheme = theme
    ? {
        ...darkTheme,
        chart: { ...darkTheme.chart, backgroundColor: undefined },
      }
    : {
        ...lightTheme,
        chart: { ...lightTheme.chart, backgroundColor: undefined },
      };
  Highcharts.setOptions(setTheme);
  const isGetGraphDataEmpty = getGraphData.xAxisCategories.length === 0;
  return isGetGraphDataEmpty ? (
    <div className="dataNotAvailableDiv">
      <span className="dataNotAvailableSpan">No Data Available</span>
    </div>
  ) : (
    <div className="highCharts-ds-container">
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        containerProps={{
          id: "container",
          style: { width: resizeOfGraph.highChartContainerWidth },
        }}
      />
    </div>
  );
};

export default DigitalSignageAreaGraph;
